/**
 * SettingsShell — the single home for governance / admin / scaffolding
 * that the Great Simplification (BACKLOG.md, PRD.md §6) moved off the
 * left rail. Every secondary page lives behind a tab here so the
 * sidebar can stay at 5 nouns + Settings.
 *
 * Each tab embeds the existing v2 page component (or the v1 page when
 * v2 hasn't been built yet). Routes for the old standalone URLs stay
 * mounted in App.js for deep-link back-compat — they just aren't
 * advertised in nav anymore.
 *
 * The active tab is reflected in the ?tab= query param so direct
 * links work, e.g. /settings?tab=workflows.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Workflow, ShieldCheck, Layers, Target as TargetIcon,
  Shield, UserCog, Bell, Sliders, Activity, HelpCircle, BookOpen,
} from "lucide-react";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import Settings from "@/pages/Settings";
import FineTuning from "@/pages/FineTuning";
import SystemMonitoring from "@/pages/SystemMonitoring";
import HelpCenter from "@/pages/HelpCenter";

import V2WorkflowsPage from "@/pages/v2/WorkflowsPage";
import V2ApprovalPoliciesPage from "@/pages/v2/ApprovalPoliciesPage";
import V2TemplatesPage from "@/pages/v2/TemplatesPage";
import V2TargetsPage from "@/pages/v2/TargetsPage";
import V2MarginRulesPage from "@/pages/v2/MarginRulesPage";
import V2UsersPage from "@/pages/v2/UsersPage";
import V2AuditPage from "@/pages/v2/AuditPage";
import V2NotificationsPage from "@/pages/v2/NotificationsPage";
import SectionRegistryPage from "@/pages/v2/SectionRegistryPage";


// Tab order matches PRD §6 — frequency-of-use first, niche-admin last.
// workflows and approvals are hidden from the tab bar but their routes/APIs
// remain active — they're triggered programmatically (send-for-review, generate-proposal).
const HIDDEN_TABS = new Set(["workflows", "approvals", "margin-rules", "targets", "fine-tuning", "audit", "notifications", "templates"]);
const MANAGER_ONLY_TABS = new Set(["users", "monitoring"]);

const TABS = [
  { value: "company",      label: "Company",         icon: Building2,   render: () => <Settings /> },
  { value: "workflows",    label: "Workflows",       icon: Workflow,    render: () => <V2WorkflowsPage /> },
  { value: "approvals",    label: "Approval policies", icon: ShieldCheck, render: () => <V2ApprovalPoliciesPage /> },
  { value: "templates",    label: "Project templates", icon: Layers,    render: () => <V2TemplatesPage /> },
  { value: "targets",      label: "Targets",         icon: TargetIcon,  render: () => <V2TargetsPage /> },
  { value: "margin-rules", label: "Margin rules",    icon: Shield,      render: () => <V2MarginRulesPage /> },
  { value: "users",        label: "Users",           icon: UserCog,     render: () => <V2UsersPage /> },
  { value: "section-registry", label: "Section registry", icon: BookOpen, render: () => <SectionRegistryPage /> },
  { value: "notifications",label: "Notifications",   icon: Bell,        render: () => <V2NotificationsPage /> },
  { value: "fine-tuning",  label: "Fine tuning",     icon: Sliders,     render: () => <FineTuning /> },
  { value: "audit",        label: "Audit log",       icon: Shield,      render: () => <V2AuditPage /> },
  { value: "monitoring",   label: "System monitoring", icon: Activity,  render: () => <SystemMonitoring /> },
  { value: "help",         label: "Help center",     icon: HelpCircle,  render: () => <HelpCenter /> },
];

const TAB_VALUES = TABS.map((t) => t.value);
const DEFAULT_TAB = "company";


export default function SettingsShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDeptHead } = useAuth();
  const params = new URLSearchParams(location.search);
  const requested = params.get("tab") || DEFAULT_TAB;
  const active = TAB_VALUES.includes(requested) ? requested : DEFAULT_TAB;

  const onTabChange = (value) => {
    const next = new URLSearchParams(location.search);
    next.set("tab", value);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
        <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
        <p className="text-xs text-slate-500">
          Governance, admin and scaffolding for the workspace. Daily tender
          work happens under the five nouns in the left rail; everything that
          isn't one of those lives here.
        </p>
      </div>

      <div className="px-4 py-3">
        <Tabs value={active} onValueChange={onTabChange}>
          {/* Tabs wrap onto a second row at narrow widths so all 14 stay reachable. */}
          <TabsList className="flex-wrap h-auto justify-start gap-1 bg-transparent p-0 mb-3">
            {TABS.filter((t) => !HIDDEN_TABS.has(t.value) && (!MANAGER_ONLY_TABS.has(t.value) || isDeptHead())).map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Icon className="w-3.5 h-3.5 mr-1" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.value} value={t.value} className="mt-0">
              {/* Inner pages bring their own padding / max-width. */}
              {active === t.value && t.render()}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
